import { Injectable } from '@angular/core';
import { UrlSettings } from '@services/auth-services/models';
import { PlatformService } from '@services/core-services/platform.service';
import { environment } from 'src/environments/environment';
import { ApplicationMode } from '@services/core-services/app.service';
import { sanitizeUrl } from '@services/utils/url';

export interface DomainInfo {
  subdomain: string | null;
  domain: string | null;
}

@Injectable({ providedIn: 'root' })
export class UrlService {
  constructor(private readonly platformService: PlatformService) {}

  getWhitelistedTopLevelDomains() {
    // TODO: Extract from env
    return [
      'app.commercengine.io',
      'app.commercengine.dev',
      'app.commercengine.com',
      'localhost:4200',
      'app.localhost:4200',
      'commercengine.io',
      'commercengine.dev',
      'commercengine.com',
      'app.ce-frontend.pages.dev',
      'ce-frontend.pages.dev',
    ];
  }

  getDomain(): string | undefined {
    return this.getUrlSettings()?.domain;
  }

  getSubdomain(): string | undefined {
    return this.getUrlSettings()?.subdomain;
  }

  redirect(url: string) {
    return this.platformService.redirect(url);
  }

  reload() {
    return this.platformService.reload();
  }

  redirectToPath(subdomain: any, path: string = 'auth/login') {
    const { protocol, baseHost } = this.getUrlSettings();
    this.redirect(`${protocol}//${subdomain}.${baseHost}/${path}`);
    return false;
  }

  redirectToBasePath(path: string = '') {
    const { protocol, baseHost } = this.getUrlSettings();
    const url = `${protocol}//${baseHost}/${path}`;
    this.redirect(url);
    return false;
  }

  url() {
    return this.platformService.url();
  }

  parseUrl(urlString: string): URL {
    try {
      return new URL(urlString);
    } catch (error: any) {
      console.error(`URL "${urlString}" cannot be parsed as url`, error.message);
      throw error;
    }
  }

  getUrlSettings(): UrlSettings {
    const whitelistDomains = this.getWhitelistedTopLevelDomains();
    const url = this.platformService.url();
    const urlEntry = url ? new URL(url) : undefined;
    const host = urlEntry?.port
      ? [urlEntry?.hostname, urlEntry.port].join(':')
      : urlEntry?.hostname;
    const protocol = urlEntry?.protocol;
    const pathname = urlEntry?.pathname;

    // First check for exact matches in whitelisted domains
    if (host && whitelistDomains.includes(host)) {
      return {
        subdomain: undefined,
        domain: host,
        host,
        protocol,
        pathname,
        baseHost: environment.baseHost,
        search: urlEntry?.searchParams,
      };
    }

    // Then check for subdomains
    for (const domain of whitelistDomains) {
      if (host?.endsWith(`.${domain}`)) {
        // Note the dot prefix
        const subdomain = host.slice(0, -(domain.length + 1));
        return {
          subdomain: subdomain || undefined,
          domain: domain,
          host,
          protocol,
          pathname,
          baseHost: environment.baseHost,
          search: urlEntry?.searchParams,
        };
      }
    }

    return {
      subdomain: undefined,
      domain: undefined,
      host: undefined,
      protocol: undefined,
    };
  }

  isValidUrl(urlString: string): boolean {
    const urlPattern = new RegExp(
      '^(https?:\\/\\/)?' + // validate protocol
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // validate domain name
        '((\\d{1,3}\\.){3}\\d{1,3}))' + // validate OR ip (v4) address
        '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // validate port and path
        '(\\?[;&a-z\\d%_.~+=-]*)?' + // validate query string
        '(\\#[-a-z\\d_]*)?$',
      'i'
    );

    return urlPattern.test(urlString);
  }

  // Regex for ULID: 26 characters, Crockford's Base32.
  private readonly ULID_REGEX = /^[0-9A-HJKMNP-TV-Z]{26}$/i;

  isUlid(segment: string): boolean {
    return this.ULID_REGEX.test(segment);
  }

  replaceAppModeInBaseUrl(url: string, mode: ApplicationMode): string {
    const newSlug = mode === ApplicationMode.Live ? 'p' : 's';
    const segments = url.split('/').filter(segment => segment !== '');

    // Find the current application mode ('s' or 'p')
    const modeIndex = segments.findIndex(segment => segment === 's' || segment === 'p');

    if (modeIndex === -1) {
      throw new Error("No application mode ('s' or 'p') found in URL");
    }

    // Split into: segments before mode, mode, segments after mode
    const segmentsBeforeMode = segments.slice(0, modeIndex);
    const segmentsAfterMode = segments.slice(modeIndex + 1);

    // Process segments after mode - check for ULID and strip if found
    let processedSegmentsAfterMode = segmentsAfterMode;
    for (let i = 0; i < segmentsAfterMode.length; i++) {
      if (this.isUlid(segmentsAfterMode[i])) {
        // Strip everything from the first ULID onwards
        processedSegmentsAfterMode = segmentsAfterMode.slice(0, i);
        break;
      }
    }

    // Reconstruct: [before mode] + [new mode] + [processed after mode]
    const result = [...segmentsBeforeMode, newSlug, ...processedSegmentsAfterMode];
    return '/' + result.join('/');
  }

  sanitizeUrl(input: string) {
    return sanitizeUrl(input);
  }
}
